import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { API, fmtMoney, applyAccent, safeRedirect, fileUrl, applyDiscount, fmtDiscount, computeVat, proposalTotals, sectionAmounts } from "../lib/api";
import Logo from "../components/Logo";
import SectionView, { ZoomableImage } from "../components/ProposalSections";
import PrivacyNotice from "../components/PrivacyNotice";
import { Check, X, ShieldCheck, Loader2, Download, PenLine, Eraser, CreditCard, MessageCircle, Clock } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function PublicProposal() {
  const { publicId } = useParams();
  const [p, setP] = useState(null);
  // Marque blanche : onglet = titre de la proposition (+ société), jamais « Acteio ».
  useDocumentTitle(
    p?.title ? (p.brand?.name || p.company ? `${p.title} · ${p.brand?.name || p.company}` : p.title) : "Proposition",
    false,
  );
  const [err, setErr] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [deposit, setDeposit] = useState(null); // null | checking | paid | pending | cancel
  const [depositInfo, setDepositInfo] = useState(null); // {amount, currency} after acceptance
  const [paying, setPaying] = useState(false);
  const [questionOpen, setQuestionOpen] = useState(false);
  const [questionText, setQuestionText] = useState("");
  const [questionSent, setQuestionSent] = useState(false);
  const sectionRefs = useRef({});
  const timeAccum = useRef({});
  const currentSection = useRef(null);
  const lastTs = useRef(Date.now());

  // Visiteur INTERNE : un utilisateur Acteio connecté (owner/membre) qui ouvre le lien
  // public depuis le même navigateur ne doit PAS gonfler les stats du prospect. Un token
  // Acteio en localStorage ⇒ interne ; un prospect n'en a jamais → on n'enregistre alors
  // aucun suivi (vues, temps de lecture, scroll). L'affichage de la proposition, lui, reste normal.
  const isInternalViewer = typeof window !== "undefined" && !!localStorage.getItem("acteio_token");

  useEffect(() => {
    api.get(`/public/proposals/${publicId}`).then((r) => {
      setP(r.data);
      if (r.data?.brand?.accent_hsl) applyAccent(r.data.brand.accent_hsl);
    }).catch(() => setErr(true));
  }, [publicId]);

  // Track time per section
  useEffect(() => {
    if (!p || isInternalViewer) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // commit previous
          commitTime();
          currentSection.current = entry.target.dataset.sid;
          setActiveSection(entry.target.dataset.sid);
          lastTs.current = Date.now();
        }
      });
    }, { threshold: 0.5 });

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));

    const commitTime = () => {
      if (currentSection.current) {
        const sid = currentSection.current;
        const dur = (Date.now() - lastTs.current) / 1000;
        timeAccum.current[sid] = (timeAccum.current[sid] || 0) + dur;
        lastTs.current = Date.now();
      }
    };

    const flush = () => {
      commitTime();
      Object.entries(timeAccum.current).forEach(([sid, dur]) => {
        if (dur < 0.5) return;
        const sec = (p.sections || []).find((s) => s.id === sid);
        api.post(`/public/proposals/${publicId}/track`, {
          type: "time", section_id: sid, section_title: sec?.title || "Section", duration: Math.round(dur),
        }).catch(() => {});
      });
      timeAccum.current = {};
    };

    const interval = setInterval(flush, 8000);
    window.addEventListener("beforeunload", flush);
    return () => { flush(); clearInterval(interval); observer.disconnect(); window.removeEventListener("beforeunload", flush); };
  }, [p, publicId, isInternalViewer]);

  // Première ouverture (compteur de vues + notification) & profondeur de scroll.
  // Le backend garde first_view idempotent (guard atomique firstViewedAt) et ne
  // retient le scroll que s'il dépasse le max enregistré.
  useEffect(() => {
    if (!p || isInternalViewer) return;
    api.post(`/public/proposals/${publicId}/track`, { type: "open" }).catch(() => {});
    let maxScroll = 0;
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = h > 0 ? Math.min(100, Math.round((window.scrollY / h) * 100)) : 100;
      if (pct > maxScroll) maxScroll = pct;
    };
    const sendScroll = () => {
      if (maxScroll > 0) api.post(`/public/proposals/${publicId}/track`, { type: "scroll", scroll: maxScroll }).catch(() => {});
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    const scrollInterval = setInterval(sendScroll, 8000);
    window.addEventListener("beforeunload", sendScroll);
    return () => { sendScroll(); window.removeEventListener("scroll", onScroll); clearInterval(scrollInterval); window.removeEventListener("beforeunload", sendScroll); };
  }, [p, publicId, isInternalViewer]);

  const startStripeDeposit = async () => {
    try {
      const { data } = await api.post(`/public/proposals/${publicId}/deposit-checkout`);
      if (data.checkout_url) { safeRedirect(data.checkout_url); return true; }
    } catch (e) { /* retryable */ }
    return false;
  };

  const startPaypalDeposit = async () => {
    try {
      const { data } = await api.post(`/public/proposals/${publicId}/paypal-checkout`);
      if (data.approval_url) { safeRedirect(data.approval_url); return true; }
    } catch (e) { /* retryable */ }
    return false;
  };

  const pollStripeDeposit = async (sid, attempts = 0) => {
    if (attempts >= 8) { setDeposit("pending"); return; }
    try {
      const { data } = await api.get(`/public/payments/status/${sid}`);
      if (data.status === "paid") { setDeposit("paid"); window.history.replaceState({}, "", window.location.pathname); return; }
      if (data.status === "expired") { setDeposit("pending"); return; }
    } catch (e) { /* retry */ }
    setTimeout(() => pollStripeDeposit(sid, attempts + 1), 2000);
  };

  const capturePaypalDeposit = async (orderId) => {
    try {
      const { data } = await api.post(`/public/payments/paypal-capture/${orderId}`);
      if (data.status === "paid") { setDeposit("paid"); window.history.replaceState({}, "", window.location.pathname); return; }
    } catch (e) { /* capture failed */ }
    setDeposit("pending");
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const paypalResult = params.get("paypal_result");
    const paypalToken = params.get("token");
    const dep = params.get("deposit");
    if (dep === "cancel" || paypalResult === "cancelled") {
      setDeposit("cancel");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (paypalResult === "success" && paypalToken) {
      setDeposit("checking");
      capturePaypalDeposit(paypalToken);
    } else if (sid) {
      setDeposit("checking");
      pollStripeDeposit(sid, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (status, payload = {}) => {
    setDeciding(true);
    try {
      const { data } = await api.post(`/public/proposals/${publicId}/decision`, { status, ...payload });
      setP((prev) => ({ ...prev, status: data.status }));
      setSignOpen(false);
      if (data.deposit_required) {
        setDepositInfo({ amount: data.deposit_amount, currency: data.deposit_currency || p.currency });
      }
    } finally { setDeciding(false); }
  };

  const payDeposit = async (method = "stripe") => {
    setPaying(true);
    const ok = method === "paypal" ? await startPaypalDeposit() : await startStripeDeposit();
    if (!ok) setPaying(false);
  };

  const sendQuestion = async () => {
    if (!questionText.trim()) return;
    try {
      await api.post(`/public/proposals/${publicId}/question`, { question: questionText.trim() });
      setQuestionSent(true);
      setQuestionOpen(false);
    } catch (e) { /* silent */ }
  };

  // Config choisie par le prospect : { [item_id]: { quantity, enabled } }. Initialisée depuis les sections.
  const [cfg, setCfg] = useState({});
  useEffect(() => {
    if (!p) return;
    const init = {};
    (p.sections || []).forEach((s) => s.type === "configurable" && (s.items || []).forEach((it) => {
      init[it.id] = { quantity: Number(it.quantity) || 0, enabled: it.optional ? (it.enabled !== false) : true };
    }));
    setCfg(init);
  }, [p]);
  const locked = p && ["acceptée", "refusée"].includes(p.status); // config figée après décision
  const setLine = (id, patch) => setCfg((c) => ({ ...c, [id]: { ...c[id], ...patch } }));

  // ── Dépendances entre lignes (requires) — miroir de resolveConfiguration serveur ──
  const cfgStateOf = (it) => cfg[it.id] || { quantity: Number(it.quantity) || 0, enabled: it.optional ? it.enabled !== false : true };
  const depSatisfied = (s, it, seen = new Set()) => {
    if (!it.requires) return true;
    if (seen.has(it.id)) return true; // cycle de config : ne pas bloquer
    seen.add(it.id);
    const parent = (s.items || []).find((x) => x.id === it.requires);
    if (!parent) return true;
    const pc = cfgStateOf(parent);
    return pc.enabled !== false && (Number(pc.quantity) || 0) >= 1 && depSatisfied(s, parent, seen);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Proposition introuvable.</div>;
  if (!p) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const sections = p.sections || [];
  // Totaux ventilés one-shot / récurrent (mensuel), toujours affichés SÉPARÉMENT (jamais
  // additionnés) : une offre peut mélanger un abonnement mensuel et des frais d'installation
  // ponctuels. `resolve` branche l'état interactif de l'offre ajustable (choix du prospect)
  // sur le même algorithme que le serveur (`proposalTotals`, prorata de remise de section
  // mixte — voir proposals.helpers.ts pour la justification).
  const resolveItem = (it, s) => {
    if (s.type !== "configurable") return { enabled: it.enabled !== false, quantity: Number(it.quantity) || 0 };
    const c = cfg[it.id];
    return {
      enabled: (c ? c.enabled : it.enabled !== false) && depSatisfied(s, it),
      quantity: c ? c.quantity : (Number(it.quantity) || 0),
    };
  };
  const totals = proposalTotals(sections, resolveItem);
  // Fiscalité : tarifs saisis HT (calculs serveur inchangés). Si la société a
  // renseigné son taux de TVA, on affiche EN PLUS le bloc TVA/TTC (sur les DEUX montants) ;
  // sinon la mention "Tarifs hors taxes — TVA applicable en sus au taux en vigueur.".
  const vatRate = p.brand?.vat_rate;
  const vatOneShot = computeVat(totals.oneShot, vatRate);
  const vatRecurring = computeVat(totals.recurring, vatRate);
  const decided = p.status === "acceptée" || p.status === "refusée";
  const expiryDate = p.expires_at ? new Date(p.expires_at) : null;
  const now = new Date();
  const isExpired = p.expired || (expiryDate && expiryDate < now);
  const expiresInDays = expiryDate ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)) : null;
  const expiresVerysSoon = expiresInDays !== null && expiresInDays >= 0 && expiresInDays <= 3;
  const brandObj = { name: p.brand?.name || p.company || "OXIWI", logo_letter: p.brand?.logo_letter || "O", logo_file_id: p.brand?.logo_file_id };

  const TYPE_LABEL = { cover: "Introduction", text: "Présentation", goals: "Objectifs", process: "Processus", stats: "Chiffres clés", team: "Équipe", pricing: "Tarification", configurable: "Offre ajustable", testimonials: "Témoignages", faq: "FAQ", image: "Visuel", video: "Vidéo" };
  const toc = sections.map((s) => ({ id: s.id, label: (s.title || "").trim() || TYPE_LABEL[s.type] || "Section" }));
  const scrollToSection = (id) => sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-surface pb-32">
      <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-border/60 sticky top-0 z-40 flex items-center justify-between px-6">
        <div className="flex items-center gap-3 min-w-0">
          <Logo brand={brandObj} />
          {p.prospect_brand && (
            <>
              <span className="text-border/60 select-none" aria-hidden>×</span>
              {p.prospect_brand.logo_file_id ? (
                <img
                  src={fileUrl(p.prospect_brand.logo_file_id)}
                  alt={p.prospect_brand.name || ""}
                  className="h-7 w-auto max-w-[100px] object-contain shrink-0"
                />
              ) : (
                <span className="font-heading font-extrabold text-base tracking-tight truncate max-w-[100px]">
                  {p.prospect_brand.name}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-4">
          <a href={`${API}/public/proposals/${publicId}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="public-download-pdf"
            className="flex items-center gap-1.5 text-xs font-semibold text-foreground hover:text-primary transition-colors">
            <Download className="w-4 h-4" /> PDF
          </a>
          <span className="hidden sm:inline text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{p.brand?.tagline || p.company}</span>
        </div>
      </header>

      {deposit && deposit !== "checking" && (
        <div data-testid="deposit-banner" className={`px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 ${deposit === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          <CreditCard className="w-4 h-4" />
          {deposit === "paid" && "Acompte réglé avec succès. Merci !"}
          {deposit === "cancel" && "Paiement de l'acompte annulé — vous pourrez le régler ultérieurement."}
          {deposit === "pending" && "Paiement en cours de traitement…"}
        </div>
      )}
      {deposit === "checking" && (
        <div data-testid="deposit-banner" className="px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 bg-primary/5 text-primary">
          <Loader2 className="w-4 h-4 animate-spin" /> Vérification du paiement de l'acompte…
        </div>
      )}
      {isExpired && !decided && (
        <div data-testid="expiry-banner" className="px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 bg-rose-50 text-rose-700">
          <Clock className="w-4 h-4" /> {expiryDate ? `Cette proposition a expiré le ${expiryDate.toLocaleDateString("fr-FR")}.` : "Cette proposition a expiré."} Contactez-nous pour obtenir une offre actualisée.
        </div>
      )}
      {expiresVerysSoon && !decided && (
        <div data-testid="expiry-soon-banner" className="px-6 py-3 text-sm font-medium flex items-center justify-center gap-2 bg-amber-50 text-amber-700">
          <Clock className="w-4 h-4" />
          {expiresInDays === 0 ? "Cette offre expire aujourd'hui." : `Cette offre expire dans ${expiresInDays} jour${expiresInDays > 1 ? "s" : ""}.`}
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 py-10 lg:grid lg:grid-cols-[230px_minmax(0,1fr)] lg:gap-10 lg:items-start">
        {/* Propal content — sticky table of contents */}
        <aside className="hidden lg:block sticky top-24" data-testid="proposal-toc">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3 px-1">Contenu de la proposition</p>
          <nav className="border-l border-border">
            {toc.map((s, i) => (
              <button key={s.id} onClick={() => scrollToSection(s.id)} data-testid={`toc-${s.id}`}
                className={`group w-full text-left flex items-start gap-2.5 pl-3 pr-2 py-1.5 -ml-px border-l-2 transition-all duration-150 ${activeSection === s.id ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}>
                <span className="text-[11px] tabular-nums mt-0.5 opacity-60">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm leading-snug">{s.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0">
          <div className="bg-white border border-border shadow-sm rounded-sm animate-fade-up max-w-3xl mx-auto lg:mx-0 overflow-hidden">
            {p.expired && !decided && (
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center gap-3">
                <svg className="w-5 h-5 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-amber-800">Cette proposition a expiré</p>
                  <p className="text-xs text-amber-700">La date limite de réponse est dépassée. Contactez l'émetteur pour obtenir une nouvelle proposition.</p>
                </div>
              </div>
            )}
            {p.expired && (!p.sections || p.sections.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                <svg className="w-16 h-16 text-muted-foreground/30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="font-heading text-xl font-bold mb-2">Proposition expirée</h2>
                <p className="text-muted-foreground max-w-sm">La date limite de réponse est dépassée. Contactez directement l'agence pour obtenir une proposition actualisée.</p>
              </div>
            ) : (
              <div className="p-8 lg:p-12 space-y-10">
                {sections.map((s) => (
                  <div key={s.id} ref={(el) => (sectionRefs.current[s.id] = el)} data-sid={s.id} className="scroll-mt-24" data-testid={`public-section-${s.id}`}>
                    {s.type === "configurable" ? (
                      <div>
                        <div className="flex items-center gap-3 mb-6">
                          <span className="w-2.5 h-2.5 bg-primary rotate-45 shrink-0" />
                          <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">{s.title}</h2>
                        </div>
                        {s.image && <ZoomableImage src={s.image} alt={s.title} className="mb-6" />}
                        <div className="space-y-2" data-testid={`cfg-section-${s.id}`}>
                          {(s.items || []).map((it) => {
                            const c = cfg[it.id] || { quantity: Number(it.quantity) || 0, enabled: it.optional ? it.enabled !== false : true };
                            const okDep = depSatisfied(s, it);
                            const eff = c.enabled && okDep; // état effectif : choix client ET dépendance satisfaite
                            const min = Number(it.min_qty) || 0, max = it.max_qty == null || it.max_qty === "" ? null : Number(it.max_qty);
                            const setQty = (q) => { let n = Math.max(min, Math.round(Number(q) || 0)); if (max != null) n = Math.min(max, n); setLine(it.id, { quantity: n }); };
                            return (
                              <div key={it.id} className={`flex flex-wrap items-center gap-3 p-3 rounded-sm border border-border ${eff ? "" : "opacity-50"}`} data-testid={`cfg-line-${it.id}`}>
                                {it.optional && <input type="checkbox" checked={eff} disabled={locked || !okDep} onChange={(e) => setLine(it.id, { enabled: e.target.checked })} />}
                                <div className="flex-1 min-w-[160px]">
                                  <p className="font-medium text-sm">{it.name}</p>
                                  {it.description && <p className="text-xs text-muted-foreground">{it.description}</p>}
                                  {!okDep && (
                                    <p className="text-xs text-amber-700 mt-0.5" data-testid={`cfg-requires-hint-${it.id}`}>
                                      Nécessite « {(s.items || []).find((x) => x.id === it.requires)?.name || "la ligne requise"} »
                                    </p>
                                  )}
                                </div>
                                <span className="text-sm font-mono text-muted-foreground">
                                  {fmtMoney(Number(it.unit_price) || 0, p.currency)} HT{it.recurrence === "mensuel" ? "/mois" : ""}
                                  {!!it.discount_type && <span className="ml-1.5 text-emerald-700 font-medium">{fmtDiscount(it.discount_type, it.discount_value, p.currency)}</span>}
                                </span>
                                {it.locked ? (
                                  <span className="text-sm w-24 text-center">× {Number(it.quantity) || 0}</span>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button disabled={locked || !eff || c.quantity <= min} onClick={() => setQty(c.quantity - 1)} className="w-7 h-7 rounded-sm border border-border disabled:opacity-40">−</button>
                                    <input type="number" value={c.quantity} disabled={locked || !eff}
                                      onChange={(e) => setLine(it.id, { quantity: e.target.value === "" ? "" : Number(e.target.value) })}
                                      onBlur={() => setQty(c.quantity === "" ? min : c.quantity)}
                                      className="w-14 text-center bg-muted rounded-sm py-1" data-testid={`cfg-qty-${it.id}`} />
                                    <button disabled={locked || !eff || (max != null && c.quantity >= max)} onClick={() => setQty(c.quantity + 1)} className="w-7 h-7 rounded-sm border border-border disabled:opacity-40">+</button>
                                  </div>
                                )}
                                <span className="w-24 text-right font-mono font-semibold">{fmtMoney(eff ? applyDiscount((Number(c.quantity) || 0) * (Number(it.unit_price) || 0), it.discount_type, it.discount_value) : 0, p.currency)}</span>
                              </div>
                            );
                          })}
                          {/* Récapitulatif TOUJOURS affiché (total mis à jour en direct selon
                              les choix du prospect) ; Sous-total/Remise seulement si remise globale.
                              Section mixte (mensuel + unique) : deux totaux séparés, jamais additionnés. */}
                          {(s.items || []).length > 0 && (() => {
                            const subtotal = (s.items || []).reduce((a, it) => {
                              const c = cfg[it.id] || { quantity: Number(it.quantity) || 0, enabled: it.optional ? it.enabled !== false : true };
                              if (!c.enabled || !depSatisfied(s, it)) return a;
                              return a + applyDiscount((Number(c.quantity) || 0) * (Number(it.unit_price) || 0), it.discount_type, it.discount_value);
                            }, 0);
                            const hasSectionDiscount = !!s.discount_type;
                            const { oneShot: sOneShot, recurring: sRecurring } = sectionAmounts(s, resolveItem);
                            const sMixed = sOneShot > 0 && sRecurring > 0;
                            return (
                              <div className="p-3 rounded-sm border border-border bg-surface text-sm space-y-1">
                                {hasSectionDiscount && (
                                  <>
                                    <div className="flex justify-between text-muted-foreground"><span>Sous-total HT</span><span className="font-mono">{fmtMoney(subtotal, p.currency)}</span></div>
                                    <div className="flex justify-between text-emerald-700 font-medium"><span>Remise globale</span><span className="font-mono">{fmtDiscount(s.discount_type, s.discount_value, p.currency)}</span></div>
                                  </>
                                )}
                                {sMixed ? (
                                  <>
                                    <div className="flex justify-between font-semibold"><span>Total HT — une fois</span><span className="font-mono">{fmtMoney(sOneShot, p.currency)}</span></div>
                                    <div className="flex justify-between font-semibold"><span>Total HT — par mois</span><span className="font-mono">{fmtMoney(sRecurring, p.currency)}/mois</span></div>
                                  </>
                                ) : (
                                  <div className="flex justify-between font-semibold">
                                    <span>Total HT{sRecurring > 0 ? " — par mois" : ""}</span>
                                    <span className="font-mono">{fmtMoney(sOneShot + sRecurring, p.currency)}{sRecurring > 0 ? "/mois" : ""}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <SectionView section={s} currency={p.currency} clientName={p.client_name} brand={p.brand} />
                    )}
                  </div>
                ))}

                {/* Récapitulatif final — deux montants SÉPARÉS (jamais additionnés : un
                    abonnement et des frais ponctuels ne forment pas un total qui ait un sens),
                    présentés en PAIRE pour qu'on lise « X une fois, puis Y par mois » d'un coup
                    d'œil. La mention « TVA en sus » est portée UNE seule fois par le bloc : elle
                    était auparavant répétée sous chaque montant (2× ici + 2× dans la barre
                    basse = 4 fois par écran). Si un seul des deux montants existe, sa cellule
                    occupe toute la largeur et le libellé passe à « Montant total ». */}
                <div className="pt-6 border-t-2 border-foreground">
                  <div className={`grid gap-px bg-border border border-border ${totals.oneShot > 0 && totals.recurring > 0 ? "sm:grid-cols-2" : ""}`}>
                    {totals.oneShot > 0 && (
                      <TotalCell
                        label={totals.recurring > 0 ? "Montant unique" : "Montant total"}
                        amount={totals.oneShot} currency={p.currency} vat={vatOneShot} vatRate={vatRate}
                        testId="public-total-one-shot" testIdVat="public-vat-block-one-shot" testIdTtc="public-total-ttc-one-shot"
                      />
                    )}
                    {totals.recurring > 0 && (
                      <TotalCell
                        label="Montant mensuel" monthly
                        amount={totals.recurring} currency={p.currency} vat={vatRecurring} vatRate={vatRate}
                        testId="public-total-recurring" testIdVat="public-vat-block-recurring" testIdTtc="public-total-ttc-recurring"
                      />
                    )}
                  </div>
                  {!vatOneShot && !vatRecurring && (
                    <p className="text-xs text-muted-foreground mt-3" data-testid="public-vat-mention">
                      Tarifs hors taxes — TVA applicable en sus au taux en vigueur.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground mt-6 max-w-3xl mx-auto lg:mx-0 text-center">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> Document sécurisé · {p.brand?.name || p.company}
            <span aria-hidden>·</span>
            <PrivacyNotice companyName={p.brand?.name || p.company} />
          </p>
        </div>
      </div>

      {/* Decision bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-border z-40">
        {questionOpen && (
          <div className="border-b border-border bg-muted/30 px-4 py-3 max-w-5xl mx-auto">
            <p className="text-sm font-medium mb-2">Votre question à l'équipe</p>
            <div className="flex gap-2">
              <textarea
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="Posez votre question ou partagez une objection…"
                rows={2}
                className="flex-1 rounded-sm border border-border bg-white px-3 py-2 text-sm resize-none outline-none focus:ring-1 focus:ring-primary"
              />
              <div className="flex flex-col gap-1">
                <button onClick={sendQuestion} disabled={!questionText.trim()}
                  className="px-4 py-2 rounded-sm bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
                  Envoyer
                </button>
                <button onClick={() => setQuestionOpen(false)} className="px-4 py-2 rounded-sm border border-border text-sm text-muted-foreground hover:bg-muted">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Alignée sur la largeur du document (max-w-5xl) : en max-w-3xl la barre était plus
            étroite que la proposition qu'elle conclut, et les montants + 3 boutons n'y tenaient
            pas sur une seule rangée (les boutons repassaient à la ligne). */}
        <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          {decided ? (
            <div className={`w-full text-center py-2 font-semibold ${p.status === "acceptée" ? "text-emerald-600" : "text-rose-600"}`} data-testid="public-decided">
              {p.status === "acceptée" ? "✓ Vous avez accepté cette proposition. Merci !" : "Proposition refusée."}
            </div>
          ) : (
            <>
              {/* Barre de décision : les deux montants CÔTE À CÔTE (et non empilés, qui
                  faisaient enfler la barre et rognaient la lecture du document). Le suffixe
                  « HT » porté par chaque montant remplace les mentions « TVA en sus » répétées
                  sous chacun ; la mention légale complète reste dans le récapitulatif du
                  document et dans le PDF. Le détail TTC n'apparaît ici que si un taux de TVA
                  est renseigné, sur une seule ligne partagée. */}
              <div>
                <div className="flex items-center gap-4 sm:gap-5">
                  {totals.oneShot > 0 && (
                    <div data-testid="public-bar-one-shot">
                      <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap">
                        {totals.recurring > 0 ? "Montant unique" : "Montant total"}
                      </p>
                      <p className="font-heading text-xl font-bold leading-tight mt-0.5 whitespace-nowrap">
                        {fmtMoney(totals.oneShot, p.currency)}
                        <span className="text-xs font-semibold text-muted-foreground ml-1">HT</span>
                      </p>
                    </div>
                  )}
                  {totals.oneShot > 0 && totals.recurring > 0 && (
                    <div className="w-px self-stretch bg-border" aria-hidden="true" />
                  )}
                  {totals.recurring > 0 && (
                    <div data-testid="public-bar-recurring">
                      <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap">Montant mensuel</p>
                      <p className="font-heading text-xl font-bold leading-tight mt-0.5 whitespace-nowrap">
                        {fmtMoney(totals.recurring, p.currency)}
                        <span className="text-xs font-semibold text-muted-foreground ml-1">HT/mois</span>
                      </p>
                    </div>
                  )}
                </div>
                {(vatOneShot || vatRecurring) && (
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    TVA {vatRate} % · TTC{" "}
                    {[vatOneShot && fmtMoney(vatOneShot.ttc, p.currency),
                      vatRecurring && `${fmtMoney(vatRecurring.ttc, p.currency)}/mois`]
                      .filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              <div className="flex gap-2 w-full sm:w-auto flex-wrap">
                {questionSent ? (
                  <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium px-3"><Check className="w-4 h-4" /> Question envoyée</span>
                ) : (
                  <button onClick={() => setQuestionOpen((v) => !v)} data-testid="public-question"
                    className="flex items-center justify-center gap-1.5 border border-border px-4 h-11 rounded-sm text-sm text-muted-foreground hover:text-foreground hover:border-foreground transition-all">
                    <MessageCircle className="w-4 h-4" /> J'ai une question
                  </button>
                )}
                <button onClick={() => decide("refusée")} disabled={deciding} data-testid="public-reject"
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 border border-border px-5 h-11 rounded-sm font-medium hover:border-destructive hover:text-destructive transition-all">
                  <X className="w-4 h-4" /> Refuser
                </button>
                <button onClick={() => setSignOpen(true)} disabled={deciding || isExpired} data-testid="public-accept"
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 bg-primary text-white px-6 h-11 rounded-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all">
                  <PenLine className="w-4 h-4" /> Accepter & signer
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <p className="max-w-3xl mx-auto px-6 pb-10 pt-2 text-[11px] leading-relaxed text-muted-foreground/70 text-center" data-testid="public-privacy-notice">
        En consultant cette proposition, votre activité de lecture et votre adresse IP sont enregistrées à des fins de
        suivi commercial par {p.brand?.name || "l'émetteur"}. Vous pouvez demander l'accès ou la suppression de ces
        données auprès de l'émetteur.
      </p>

      {signOpen && (
        <SignatureDialog
          deciding={deciding}
          brandName={p.brand?.name || p.company}
          onClose={() => setSignOpen(false)}
          onSign={(signer_name, signature_data) => {
            // N'inclure `configuration` que si l'offre a une section ajustable (sinon
            // le chemin d'acceptation reste strictement inchangé côté serveur).
            const hasConfigurable = (p.sections || []).some((s) => s.type === "configurable");
            const configuration = [];
            if (hasConfigurable) (p.sections || []).forEach((s) => s.type === "configurable" && (s.items || []).forEach((it) => {
              const c = cfg[it.id]; if (c) configuration.push({ section_id: s.id, item_id: it.id, quantity: c.quantity, enabled: c.enabled });
            }));
            decide("acceptée", { signer_name, signature_data, consent: true, ...(hasConfigurable ? { configuration } : {}) });
          }}
        />
      )}

      {depositInfo && deposit !== "paid" && (
        <DepositDialog
          info={depositInfo}
          brand={p.brand}
          totals={totals}
          paying={paying}
          onPay={payDeposit}
          onClose={() => setDepositInfo(null)}
        />
      )}

    </div>
  );
}

/**
 * Cellule d'un montant du récapitulatif final : libellé discret, montant en vedette avec son
 * suffixe HT (ou HT/mois), et le détail TVA / Total TTC seulement si un taux est renseigné.
 * Mutualisée entre le montant unique et le montant mensuel — les deux cellules ne diffèrent
 * que par leur libellé, leur suffixe et leurs data-testid ; les dupliquer laissait deux blocs
 * de mise en forme à garder synchronisés à la main.
 */
function TotalCell({ label, amount, currency, vat, vatRate, monthly = false, testId, testIdVat, testIdTtc }) {
  const suffix = monthly ? "/mois" : "";
  return (
    <div className="bg-background p-5">
      <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <p className="font-heading text-3xl font-bold mt-1.5 leading-none" data-testid={testId}>
        {fmtMoney(amount, currency)}
        <span className="text-sm font-semibold text-muted-foreground ml-1.5">HT{suffix}</span>
      </p>
      {vat && (
        <div className="mt-3 pt-3 border-t border-border/70 space-y-1 text-sm" data-testid={testIdVat}>
          <div className="flex items-center justify-between text-muted-foreground">
            <span>TVA {vatRate} %</span>
            <span className="font-mono">{fmtMoney(vat.vat, currency)}</span>
          </div>
          <div className="flex items-center justify-between font-semibold">
            <span>Total TTC</span>
            <span className="font-mono" data-testid={testIdTtc}>{fmtMoney(vat.ttc, currency)}{suffix}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// L'acompte porte TOUJOURS sur le one-shot uniquement (jamais sur un abonnement) :
// le solde restant à régler se calcule donc sur `totals.oneShot`, jamais sur un total
// mélangé. L'abonnement mensuel, lui, n'est jamais concerné par l'acompte — affiché à
// part, à titre d'information, quand il existe.
function DepositDialog({ info, brand, totals, paying, onPay, onClose }) {
  const solde = Math.max(totals.oneShot - info.amount, 0);
  const pct = brand?.deposit_type === "percentage" && brand?.deposit_value ? `${brand.deposit_value} %` : null;
  const hasStripe = !!brand?.stripe_key_set;
  const hasPaypal = !!brand?.paypal_enabled;
  const bothMethods = hasStripe && hasPaypal;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="deposit-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-xl font-bold">Acompte à régler</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Votre proposition est acceptée ✓. Pour la valider définitivement, un acompte est demandé.</p>

        <div className="rounded-sm border border-border divide-y divide-border mb-5" data-testid="deposit-recap">
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm">Acompte{pct ? ` (${pct})` : ""}</span>
            <span className="font-heading font-bold" data-testid="deposit-amount">{fmtMoney(info.amount, info.currency)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-muted-foreground">Solde à régler ultérieurement</span>
            <span className="text-sm text-muted-foreground">{fmtMoney(solde, info.currency)}</span>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-foreground text-white">
            <span className="text-sm font-semibold">Total une fois</span>
            <span className="font-heading font-bold">{fmtMoney(totals.oneShot, info.currency)}</span>
          </div>
          {totals.recurring > 0 && (
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-muted-foreground">Abonnement (facturé séparément)</span>
              <span className="text-sm text-muted-foreground">{fmtMoney(totals.recurring, info.currency)}/mois</span>
            </div>
          )}
        </div>

        {bothMethods && (
          <p className="text-xs text-center text-muted-foreground mb-3">Choisissez votre mode de paiement</p>
        )}

        <div className={`${bothMethods ? "flex flex-col gap-2" : "flex gap-2"}`}>
          {!bothMethods && (
            <button onClick={onClose} disabled={paying} data-testid="deposit-later"
              className="flex-1 h-11 border border-border rounded-sm font-medium hover:bg-muted transition-colors">Plus tard</button>
          )}

          {hasStripe && (
            <button onClick={() => onPay("stripe")} disabled={paying} data-testid="deposit-pay"
              className={`${bothMethods ? "w-full" : "flex-[1.5]"} h-11 bg-primary text-white rounded-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5`}>
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Payer par carte {!bothMethods && `— ${fmtMoney(info.amount, info.currency)}`}
            </button>
          )}

          {hasPaypal && (
            <button onClick={() => onPay("paypal")} disabled={paying} data-testid="deposit-pay-paypal"
              className={`${bothMethods ? "w-full" : "flex-[1.5]"} h-11 bg-[#003087] text-white rounded-sm font-semibold hover:bg-[#003087]/90 transition-colors flex items-center justify-center gap-1.5`}>
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.067 8.478c.492.315.844.825.945 1.406.46 2.67-1.63 4.777-4.16 4.777H15.6l-.75 4.758H12.6l.32-2.02H9.667L8.4 21.9H5.867l3.2-20.257h5.387c2.52 0 4.2 1.614 4.2 3.886 0 .993-.32 1.934-.92 2.673.427.04.84.133 1.4.276zm-5.76 4.383c1.334 0 2.4-.95 2.4-2.107 0-.72-.48-1.26-1.2-1.26H13.2l-.48 3.367h1.587zm1.2-6.24c1.334 0 2.4-.95 2.4-2.107 0-.72-.48-1.26-1.2-1.26h-2.4l-.48 3.367h1.68z" />
                </svg>
              )}
              Payer via PayPal {!bothMethods && `— ${fmtMoney(info.amount, info.currency)}`}
            </button>
          )}

          {bothMethods && (
            <button onClick={onClose} disabled={paying} data-testid="deposit-later"
              className="w-full h-9 border border-border rounded-sm text-sm font-medium hover:bg-muted transition-colors mt-1">Plus tard</button>
          )}
        </div>

        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-4">
          <ShieldCheck className="w-3.5 h-3.5" /> Paiement sécurisé
          {hasStripe && " · Stripe"}
          {hasStripe && hasPaypal && " &"}
          {hasPaypal && " PayPal"}
        </p>
      </div>
    </div>
  );
}

function SignatureDialog({ onClose, onSign, deciding, brandName }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Haute résolution sur écrans Retina / mobile
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";

    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = getPos(e); ctx.beginPath(); ctx.moveTo(x, y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = getPos(e); ctx.lineTo(x, y); ctx.stroke(); hasDrawn.current = true; };
    const end = () => { drawing.current = false; };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const r = canvas.getBoundingClientRect();
    canvas.getContext("2d").clearRect(0, 0, r.width * dpr, r.height * dpr);
    hasDrawn.current = false;
  };

  const submit = () => {
    setError("");
    if (!name.trim()) return setError("Indiquez votre nom complet.");
    if (!hasDrawn.current) return setError("Veuillez dessiner votre signature.");
    if (!consent) return setError("Vous devez accepter la valeur juridique de la signature.");
    onSign(name.trim(), canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="signature-dialog">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-6 animate-fade-up">
        <div className="flex items-center gap-2 mb-1">
          <PenLine className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-xl font-bold">Signer la proposition</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Votre signature électronique vaut acceptation de la proposition.</p>

        <label className="text-sm font-medium">Nom complet</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="signature-name"
          placeholder="Prénom Nom" className="w-full mt-1.5 mb-4 h-11 px-3 border border-border rounded-sm outline-none focus:border-primary" />

        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium">Votre signature</label>
          <button onClick={clear} data-testid="signature-clear" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <Eraser className="w-3.5 h-3.5" /> Effacer
          </button>
        </div>
        <canvas ref={canvasRef} width={400} height={160} data-testid="signature-canvas"
          className="w-full h-40 border-2 border-dashed border-border rounded-sm bg-surface touch-none cursor-crosshair" />

        <label className="flex items-start gap-2 mt-4 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="signature-consent" className="mt-0.5 accent-current" style={{ accentColor: "hsl(var(--primary))" }} />
          <span className="text-xs text-muted-foreground">J'accepte cette proposition et reconnais que ma signature électronique a valeur d'engagement.</span>
        </label>

        {error && <p className="text-xs text-rose-600 mt-3" data-testid="signature-error">{error}</p>}

        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
          En signant, vos nom, signature et adresse IP sont enregistrés comme preuve.{" "}
          <PrivacyNotice companyName={brandName} testIdSuffix="-signature" className="underline decoration-dotted underline-offset-2 hover:text-foreground transition-colors" />
        </p>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} disabled={deciding} data-testid="signature-cancel"
            className="flex-1 h-11 border border-border rounded-sm font-medium hover:bg-muted transition-colors">Annuler</button>
          <button onClick={submit} disabled={deciding} data-testid="signature-submit"
            className="flex-1 h-11 bg-primary text-white rounded-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5">
            {deciding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirmer la signature
          </button>
        </div>
      </div>
    </div>
  );
}
